import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, TreeDeciduous, Leaf } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";

const LeaderboardPage = () => {
  const [rows, setRows] = useState([]);
  const { user } = useAuth();
  useSEO({
    title: "Sustainability Leaderboard – Top Eco Shoppers | Ecowallet",
    description: "See the top sustainable shoppers on Ecowallet. Compete on Green Points, carbon saved and trees planted.",
    keywords: "sustainability leaderboard, sustainable shopping challenges, eco shopping rewards",
  });
  useEffect(() => { api.get("/leaderboard").then((r) => setRows(r.data)); }, []);

  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-10">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Impact Leaderboard</h1>
        <p className="text-muted-foreground mt-3">Top eco-warriors making the biggest difference this month.</p>
      </div>

      {/* Podium */}
      {top3.length === 3 && (
        <div className="grid grid-cols-3 gap-4 items-end mb-10" data-testid="podium">
          {[top3[1], top3[0], top3[2]].map((u, i) => {
            const displayRank = [2, 1, 3][i];
            const height = [180, 220, 160][i];
            return (
              <div key={u.user_id} className="text-center">
                <div className="mb-3 flex flex-col items-center">
                  <img src={u.avatar} alt="" className="h-16 w-16 rounded-full ring-4 ring-primary/20 bg-muted" />
                  <div className="mt-2 font-semibold">{u.name}</div>
                  <div className="mono text-xs text-muted-foreground">{u.points.toLocaleString()} pts</div>
                </div>
                <div
                  className="rounded-t-3xl card-premium grid place-items-center"
                  style={{ height, background: displayRank === 1 ? "linear-gradient(180deg,#FCD34D,#F59E0B)" : displayRank === 2 ? "linear-gradient(180deg,#E5E7EB,#9CA3AF)" : "linear-gradient(180deg,#FCA97F,#D97706)" }}
                >
                  <div className="text-5xl font-black text-white/95 mono">#{displayRank}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card-premium overflow-hidden" data-testid="leaderboard-table">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border text-xs uppercase tracking-widest text-muted-foreground font-semibold">
          <div className="col-span-1">Rank</div>
          <div className="col-span-5">Player</div>
          <div className="col-span-2 text-right">Points</div>
          <div className="col-span-2 text-right">Carbon Saved</div>
          <div className="col-span-2 text-right">Trees</div>
        </div>
        {rest.map((u) => {
          const isMe = user && u.user_id === user.id;
          return (
            <div
              key={u.user_id}
              className={`grid grid-cols-12 gap-4 px-6 py-4 items-center border-b border-border/50 last:border-b-0 ${
                isMe ? "bg-emerald-50/70 dark:bg-emerald-900/20" : "hover:bg-muted/40"
              }`}
              data-testid={`leaderboard-row-${u.rank}`}
            >
              <div className="col-span-1 mono font-bold text-lg">#{u.rank}</div>
              <div className="col-span-5 flex items-center gap-3">
                <img src={u.avatar} alt="" className="h-9 w-9 rounded-full bg-muted" />
                <div>
                  <div className="font-semibold">{u.name}{isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-primary">You</span>}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest flex items-center gap-1">
                    <Trophy className="h-3 w-3" /> {u.badge.name}
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-right mono font-bold">{u.points.toLocaleString()}</div>
              <div className="col-span-2 text-right mono text-sm flex items-center justify-end gap-1"><Leaf className="h-3.5 w-3.5 text-emerald-600" />{u.carbon_saved_kg} kg</div>
              <div className="col-span-2 text-right mono text-sm flex items-center justify-end gap-1"><TreeDeciduous className="h-3.5 w-3.5 text-emerald-600" />{u.trees}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaderboardPage;
